import { useEffect, useState } from "react";
import tokenService from "../../services/token-service";
import { fetchUserApplications } from "../../utils/application-service";
import Loader from "../../components/common/loader";
import { useProfileMenu } from "../../hooks/use-profile-menu";
import { Briefcase } from "lucide-react";

export default function DashboardHome() {
  const [applications, setApplications] = useState([]);
  const userDetails = tokenService.getUserDetails();
  const token = tokenService.getToken();
  const [loading, setLoading] = useState(true);
  const { handleBecomeMarketing, meJobLoading } = useProfileMenu();

  const [user, setUser] = useState(() => {
    const details = tokenService.getUserDetails() || {};
    return {
      _id: details._id,
      fullName: details.fullName,
      email: details.email,
      phoneNumber: details.phoneNumber,
      image: details.image || "/static/media/default-avatar.png",
      district: details.district || "Not Specified",
      tehsil: details.tehsil || "Not Specified",
    };
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchUserApplications(userDetails?._id, token);
        if (data) {
          const { user: apiUser, applications: apiApps } = data;
          if (apiUser) {
            setUser({
              _id: apiUser._id,
              fullName: apiUser.fullName,
              email: apiUser.email,
              phoneNumber: apiUser.phoneNumber,
              image: apiUser.image || userDetails?.image || "/static/media/default-avatar.png",
              district: apiUser.district || userDetails?.district || "Not Specified",
              tehsil: apiUser.tehsil || userDetails?.tehsil || "Not Specified",
            });
          }
          if (apiApps && apiApps.length > 0) {
            setApplications(apiApps);
          }
        }
      } catch (err) {
        console.warn("Could not load user applications:", err);
        // Soft error: keep applications empty but show dashboard (e.g. 404 No Application).
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [token, userDetails?._id]);

  if (loading) return <Loader/>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Profile Section - Left Sidebar */}
      <div className="lg:col-span-1">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-md dark:shadow-none overflow-hidden">
          <div
            className="w-full py-4 flex justify-center items-center bg-[#EFA938] dark:bg-[#b36f23]"
          >
            <img
              className="w-24 h-24 rounded-full border-4 border-white"
              src={user.image}
              alt="User"
            />
          </div>
          <div className="p-6 flex flex-col items-center text-center space-y-2 bg-white dark:bg-transparent">
            <UserInfo label="Name" value={user.fullName} />
            <UserInfo label="Phone Number" value={user.phoneNumber} />
            <UserInfo label="Email" value={user.email} />
            <UserInfo label="District" value={user.district} />
            <UserInfo label="Tehsil" value={user.tehsil} />
          </div>
        </div>
      </div>

      {/* Job Applications Section - Right Content */}
      <div className="lg:col-span-3">
        <div className="bg-white dark:bg-slate-800 dark:text-gray-100 p-6 rounded-2xl shadow-md dark:shadow-none">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            📋 Recent Job Applications
          </h2>
          {applications.length === 0 ? (
            <div className="text-center py-8 px-4">
              <p className="text-gray-500 mb-6">
                You haven't submitted any job applications yet.
              </p>
              <button
                onClick={handleBecomeMarketing}
                disabled={meJobLoading}
                className="inline-flex items-center gap-2 rounded-xl bg-[#EFA938] hover:bg-[#d8962c] text-white px-6 py-3 font-semibold shadow-md transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-75 disabled:pointer-events-none"
              >
                {meJobLoading ? (
                  <span className="animate-pulse">Loading Job...</span>
                ) : (
                  <>
                    <Briefcase className="h-5 w-5" />
                    Apply for Marketing Executive Role
                  </>
                )}
              </button>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-[#EFA938] to-[#f5c36a] dark:from-[#b36f23] dark:to-[#d9a24a]">
                <tr className="border-b">
                  <th className="text-left p-4 text-white font-semibold uppercase tracking-wide">
                  Job Title
                  </th>
                  <th className="text-left p-4 text-white font-semibold uppercase tracking-wide  ">
                    Location
                  </th>
                  <th className="text-left p-4 text-white font-semibold uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-left p-4 text-white font-semibold uppercase tracking-wide">
                    Applied Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-700">
                {applications.map((application) => (
                   <tr
                      key={application._id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="p-3 text-gray-800">
                        {application.jobId?.title || "N/A"}
                      </td>
                      <td className="p-3 text-gray-800">
                        {application.jobId?.location || "N/A"}
                      </td>
                      <td className="p-3 text-gray-800">
                       <span
                        className={`px-4 py-1.5 rounded-full text-sm font-medium inline-block ${
                          getStatusColor(application.status)
                        }`}
                      >
                        {application.status}
                      </span>
                      </td>
                      <td className="p-3 text-gray-800">
                        {new Date(application.appliedDate).toDateString()}
                      </td>
                    </tr>
                )
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const UserInfo = ({ label, value }) => (
  <div>
    <h3 className="text-gray-400 dark:text-gray-300 text-sm">{label}</h3>
    <p className="text-gray-600 dark:text-gray-100 font-medium">{value || "—"}</p>
  </div>
);

const getStatusColor = (status) => {
  switch (status) {
    // 🟦 Job Application
    case "Applied":
      return "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-200";

    // 🟡 Initial Test
    case "Initial Test Submitted":
      return "bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-200";
    case "Initial Test Passed":
      return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200";
    case "Initial Test Failed":
      return "bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300";

    // 🟢 Training
    case "Training Completed":
      return "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-200";

    // 🟢 Final Test
    case "Final Test Submitted":
      return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200";
    case "Final Test Passed":
      return "bg-lime-100 text-lime-700 dark:bg-lime-900 dark:text-lime-200";
    case "Final Test Failed":
      return "bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300";

    // 🟩 Final Decision
    case "Approved":
      return "bg-green-200 text-green-800 font-semibold dark:bg-green-900 dark:text-green-100";
    case "Rejected":
      return "bg-rose-100 text-rose-700 font-semibold dark:bg-rose-900 dark:text-rose-200";

    // ⚪ Default
    default:
      return "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300";
  }
};

